import { useCallback } from 'react'
import { usePermission } from '@/lib/permissions'
import { useStorageRoots } from '@/hooks/useStorage'
import { useFacilityLocations } from '@/hooks/useFacility'
import { fullLocation, placesByUnitCode } from '@/lib/facilityLocation'
import type { LocationTree } from '@/api/facilityApi'

// One address map per load of the two queries, shared by every component that
// shows a location: a table renders one per row, and each building the map
// over the whole site was the cost. Keyed on the query results themselves, so
// a refetch makes a new one and the old is collected with its data.
const cache = new WeakMap<object, WeakMap<object, Map<string, string>>>()
const EMPTY = new Map<string, string>()

function placesFor(tree: LocationTree, units: { code: string; equipmentId: string | null }[]): Map<string, string> {
  let byUnits = cache.get(tree)
  if (!byUnits) {
    byUnits = new WeakMap()
    cache.set(tree, byUnits)
  }
  let places = byUnits.get(units)
  if (!places) {
    places = placesByUnitCode(tree, units)
    byUnits.set(units, places)
  }
  return places
}

/**
 * Where each storage unit stands, by unit code (z8nrz7fw25), so a location
 * like "LN2-02/R3/B04/A1" can be read with its Facility › Building › Floor ›
 * Room in front. Empty while loading or for someone who may read neither
 * equipment nor inventory: a location then shows as it always did.
 */
export function useStoragePlaces(): Map<string, string> {
  const { can } = usePermission()
  const allowed = can('equipment.read') || can('inventory.view')
  const locations = useFacilityLocations(allowed)
  // The units themselves, for their codes and equipment. Not the capacity
  // summary: that counts every slot in the tenant, and every screen showing a
  // location (the procedure page's always-mounted product pickers among them)
  // paid for it on load (z8nrz7fzk7).
  const units = useStorageRoots(allowed)
  return locations.data && units.data ? placesFor(locations.data, units.data) : EMPTY
}

/**
 * How every screen writes a storage location (z8nrz7fw25): the full address,
 * Facility › Building › Floor › Room › unit › … › position. Use this wherever
 * a location is shown instead of `formatLocation`, which writes only the part
 * inside the unit.
 */
export function useLocationText(): (path: string | null | undefined) => string {
  const places = useStoragePlaces()
  return useCallback((path: string | null | undefined) => fullLocation(path, places), [places])
}
